import type { ComponentType, LazyExoticComponent } from 'react';
import { lazy } from 'react';
import {
  CalendarDays,
  LayoutDashboard,
  ListChecks,
  Settings,
  TrendingUp,
} from 'lucide-react';
import { routes } from '@config/routes';
import { featureBacktesting, featureBacktestingComingSoon } from '@config/env';
import { BacktestingAiIcon } from '@shared/components/icons/BacktestingAiIcon';

export interface FeatureModule {
  /** Stable identifier (used as route key + telemetry tag). */
  id: string;
  /** URL path mounted under the AppShell. */
  path: string;
  /** Display label in the sidebar. */
  navLabel: string;
  /** Sidebar icon. A lucide icon or any component that accepts `className` — the rail sizes it
   *  and drives its colour through `currentColor` (2026-07-25: widened for the Figma sparkle). */
  navIcon: ComponentType<{ className?: string }>;
  /** Code-split entry — default export must be the Page component. */
  Page: LazyExoticComponent<ComponentType>;
  /** Optional: hide from the main nav (sub-pages, etc.). */
  hideFromNav?: boolean;
  /** Extra paths that should keep this nav item highlighted (e.g. an in-page view that lives on a
   *  sibling route). The item still navigates to `path`. */
  navAliases?: readonly string[];
  /** Pro-gated feature — `Sidebar` overlays a small lock glyph on the icon for a core-tier caller
   *  (`useTier().isPro === false`). The item stays clickable either way; it just lands on the
   *  in-page `ProUpsell` instead of a dead click. */
  requiresPro?: boolean;
  /** Feature-flagged OFF when false — the entry is dropped from the exported manifest entirely, so
   *  BOTH the Sidebar and the router stop seeing it. Omitted means always on. */
  enabled?: boolean;
}

/**
 * Single source of truth for routable features.
 *
 * Adding a new feature is two steps: (1) create src/features/<id>/ with an index.ts
 * that has the Page as the default export; (2) append an entry below. The router
 * and the sidebar both consume this list — no edits anywhere else.
 *
 * Each entry uses `lazy(() => import(...))` so the feature ships as its own JS chunk.
 */
const allFeatures: FeatureModule[] = [
  {
    id: 'dashboard',
    path: routes.dashboard,
    navLabel: 'Dashboard',
    navIcon: LayoutDashboard,
    Page: lazy(() => import('@features/dashboard')),
  },
  // "Trades" in the user's vocabulary = round-trip (open→flat cycle). It happens to live at
  // /trades (route key `correlations`) and render via the CorrelationsPage today; the formal
  // rename to Trade / Execution is queued at trade-vs-execution-rename.md. Until that lands,
  // this manifest is what makes the menu show the correct unit.
  {
    id: 'correlations',
    path: routes.correlations,
    navLabel: 'Trades',
    navIcon: ListChecks,
    Page: lazy(() => import('@features/correlations')),
    // Renders the new two-tab Trades page (Closed trades + Open positions). The flat per-fill
    // "Fills" rail entry was removed — fills now appear nested under their closed trade.
  },
  // Profit / Loss sits directly under Trades in the rail (product ask) — the two most-used views
  // adjacent. 2026-07-09 merge: the former PnL / By-symbols / By-time / Time-in-trade rail items
  // became SUB-TABS of this one page (Overview · Symbols · Time · Time in Trade); the three legacy
  // routes stay routable below as hidden redirect entries, and the aliases keep this icon lit
  // during their redirect.
  {
    id: 'pnl',
    path: routes.pnl,
    // 2026-07-12: renamed "Profit / Loss" → "Advanced Metrics" (product); drives the sidebar
    // hover tooltip + aria-label. Route/id stay `/pnl` to preserve deep links.
    navLabel: 'Advanced Metrics',
    navIcon: TrendingUp,
    Page: lazy(() => import('@features/statistics/pnl')),
    navAliases: [routes.bySymbols, routes.byTime, routes.timeInTrade],
    // Pro-gated (2026-08-13), same treatment as `backtesting` below: `ProfitLossPage` itself does
    // the `useTier()` check and renders `ProUpsell` in place of all 4 sub-tabs for a core-tier
    // caller. The three legacy routes (`by-symbols`/`by-time`/`time-in-trade` below) redirect INTO
    // this same page, so the gate already covers them — they stay unmarked because `hideFromNav`
    // keeps them out of the Sidebar entirely (nothing there to lock).
    requiresPro: true,
  },
  // "Backtesting AI" tab (renamed from "Backtesting" 2026-07-25), placed right after Trades +
  // Advanced Metrics (PnL) in the rail. Uses the Figma sparkle instead of a lucide icon.
  {
    id: 'backtesting',
    path: routes.backtesting,
    navLabel: 'Backtesting AI',
    navIcon: BacktestingAiIcon,
    // With the backtester off and `featureBacktestingComingSoon` on (owner, 2026-11-03: the AI features as
    // "Coming soon" in production), the rail item and /backtesting come back with a Coming soon page, its own
    // chunk, which imports nothing from the backtester.
    Page: featureBacktesting
      ? lazy(() => import('@features/backtesting'))
      : lazy(() => import('@features/backtesting/pages/BacktestingComingSoonPage')),
    // Feature-flagged (2026-08-17): ON in dev, OFF in production until the feature ships. Dropping
    // the entry removes the rail item and the /backtesting route together — see `enabled` above and
    // `featureBacktesting` in config/env. The sibling results route lives in router.tsx and carries
    // its own guard (it stays off with the Coming soon page).
    enabled: featureBacktesting || featureBacktestingComingSoon,
    // NOT `requiresPro` since 2026-08-14 (user ruling: "this feature shouldn't be hidden by pro
    // subscription, if the user has credits good, if not, they will not be able to use this tab
    // properly") — AI CREDITS meter this feature, not tier, so the rail carries no lock and the
    // page renders the workspace for every tier. `pnl` above is now the only Pro-locked entry.
  },
  {
    id: 'by-symbols',
    path: routes.bySymbols,
    navLabel: 'By symbols',
    navIcon: TrendingUp,
    Page: lazy(() => import('@features/statistics/by-symbols')),
    hideFromNav: true, // redirects to /pnl?view=symbols (2026-07-09 merge)
  },
  {
    id: 'by-time',
    path: routes.byTime,
    navLabel: 'By time',
    navIcon: TrendingUp,
    Page: lazy(() => import('@features/statistics/by-time')),
    hideFromNav: true, // redirects to /pnl?view=time (2026-07-09 merge)
  },
  {
    id: 'calendar',
    path: routes.calendar,
    navLabel: 'Calendar',
    navIcon: CalendarDays,
    Page: lazy(() => import('@features/calendar')),
  },
  {
    id: 'time-in-trade',
    path: routes.timeInTrade,
    navLabel: 'P&L by Time in Trade',
    navIcon: TrendingUp,
    Page: lazy(() => import('@features/statistics/time-in-trade')),
    hideFromNav: true, // redirects to /pnl?view=time-in-trade (2026-07-09 merge)
  },
  {
    id: 'settings',
    path: routes.settings,
    navLabel: 'Settings',
    navIcon: Settings,
    Page: lazy(() => import('@features/settings')),
    // Reached from the sidebar profile menu (ProfileMenu) instead of the icon rail.
    hideFromNav: true,
  },
];

/**
 * The routable features THIS build ships.
 *
 * Filtered once, here, rather than at each consumer: `router.tsx` and `Sidebar.tsx` both read this
 * list, so a single filter removes a flagged-off feature's nav item and its route together. Gating
 * only the Sidebar would have left the page reachable by URL — a flag that hides a feature without
 * closing the door is not a flag.
 *
 * `enabled === undefined` means always on, so every existing entry is untouched.
 */
export const featureManifest: FeatureModule[] = allFeatures.filter(f => f.enabled !== false);
